<i18n>
[
    "browse__filter--title",
    "browse__filterSort--title",
]
</i18n>

<template>
    <UrbnModalLauncher
        :modalOptions="{
            ...catalogFilterGroupModal,
            title: getTitleText,
            props: { catalogContext },
        }"
        :variant="'none'"
        :data-product-tour-lightbox="productTourAttributes"
        type="button"
        class="c-filter-group-menu-launcher o-flex-vertical-center o-flex-horizontal-center u-text--gray"
    >
        <BaseIcon
            icon="catalog--filter"
            size="24px"
            color="gray"
            class="c-filter-group-menu-launcher__filter-icon"
        />
        <span class="o-text--caption">
            {{ getTitleText }}
            <span
                v-if="filterOptionsCount"
                class="c-filter-group-menu-launcher__count u-text--color-primary"
            >
                ({{ filterOptionsCount }})
            </span>
        </span>
        <template v-if="mediaQueries.isLargish && selectedFilterOptionsText">
            <span class="c-filter-group-menu-launcher__selected-filters o-text--caption">
                {{ selectedFilterOptionsText }}
            </span>
            <BaseIcon
                icon="global--field-clear"
                size="16px"
                color="gray"
                class="c-filter-group-menu-launcher__clear-icon"
                @click.prevent.stop="emit('reset-filters')"
            />
        </template>
    </UrbnModalLauncher>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useMediaQueries } from '~coreModules/core/js/composables/useMediaQueries';
import { useState, useGetters } from '~coreModules/core/js/composables/vuexHelpers';

import UrbnModalLauncher from '~coreModules/modals/components/UrbnModalLauncher.vue';

const props = defineProps({
    selectedFilterOptionsText: {
        type: String,
        default: '',
    },
    selectedFilterOptionsCount: {
        type: Number,
        default: 0,
    },
    catalogContext: {
        type: String,
        required: true,
    },
    catalogFilterGroupModal: {
        type: Object,
        required: true,
    },
    productTourAttributes: {
        type: String,
        default: undefined,
    },
});

const { t } = useI18n();
const mediaQueries = useMediaQueries();

const emit = defineEmits(['reset-filters']);

/* eslint-disable vue/no-setup-props-reactivity-loss */
const { selectedSizeList } = useGetters(props.catalogContext, ['selectedSizeList']);

const { availabilityFilterIsActive } = useState(props.catalogContext, {
    availabilityFilterIsActive: state => state.catalog.availabilityFilterIsActive,
});
/* eslint-enable vue/no-setup-props-reactivity-loss */

const getTitleText = computed(() => (
    !mediaQueries.isSmallish ?
        t('browse__filter--title') :
        t('browse__filterSort--title')
));

const filterOptionsCount = computed(() => {
    if (mediaQueries.isLargish) return 0;

    let count = props.selectedFilterOptionsCount;

    const selectedSizeCount = selectedSizeList.value ? selectedSizeList.value?.split(',').length : 0;
    count += selectedSizeCount;

    if (mediaQueries.isSmallish) {
        const availabilityFilterCount = availabilityFilterIsActive.value ? 1 : 0;
        count += availabilityFilterCount;
    }

    return count;
});
</script>

<style lang="scss">
    .c-filter-group-menu-launcher {
        text-transform: capitalize;
        min-width: 150px;
        padding: 0 $nu-spacer-2;
        background-color: $nu-white;
        border-radius: 2px;
        height: 40px;
        border: 0 none;
        flex: 1;
        cursor: pointer;

        @include breakpoint(medium) {
            flex: unset;
        }

        &__filter-icon {
            margin-right: $nu-spacer-1pt5;

            svg {
                margin: 0;
            }
        }

        &__count {
            margin-left: $nu-spacer-1;
        }

        &__selected-filters {
            display: none;
            color: $nu-primary;

            @include breakpoint(large) {
                max-width: 105px;
                display: inline-block;
                margin-left: $nu-spacer-1;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
            }

            @include breakpoint(extra-large) {
                max-width: 300px;
            }
        }

        &__clear-icon {
            margin-left: $nu-spacer-1pt5;
            position: relative;
            cursor: pointer;

            svg {
                margin-right: 0;
            }
        }
    }

</style>
